import request from '@/utils/request';
import type { PropEntity } from '@/api';
import { RParam, type ApiResult, type R, type RL, type RP } from '@/api';
import type { DictionaryData } from './model';
import LzdAjax from '@/utils/lzd-ajax';
import type { TenantDictData } from '@/api/bsl/tenant/dict/types';

const BASE_URL = '/bsl/tenant/dict/data';

/**
 * 分页查询字典数据
 */
export async function pageDictionaryData(params: RP) {
  const res = await request.post<R<RL<DictionaryData>>>(
    `${BASE_URL}/query/page`,
    params
  );
  if (res.data.code === 0) {
    return res.data.data;
  }
  return Promise.reject(new Error(res.data.message));
}

/**
 * 查询字典数据列表
 */
export async function listDictionaryData(params: RP) {
  return LzdAjax.post<TenantDictData[]>(
    `${BASE_URL}/query/list`,
    params,
    true,
    true
  ).then((v) => v.data);
}

/**
 * 添加字典数据
 */
export async function addDictionaryData(data: DictionaryData) {
  const res = await request.post<ApiResult<unknown>>(
    `${BASE_URL}/insert`,
    data
  );
  return res.data.code === 0 ? res.data : Promise.reject(res.data);
}

/**
 * 批量添加字典数据
 */
export async function batchAddDictionaryData(data: DictionaryData[]) {
  const res = await request.post<R<unknown>>(`${BASE_URL}/batch/insert`, data);
  return res.data.code === 0 ? res.data : Promise.reject(res.data);
}

/**
 * 拖拽排序
 */
export async function updateSortNumber(items: DictionaryData[]) {
  // 存储排序号
  const updateSort: Record<number, number> = {};

  return LzdAjax.post(
    BASE_URL + '/manager/update/sort-number',
    {
      pks: items.map((v) => {
        updateSort[v.seq!] = v.sortNumber!;
        return v.seq!;
      }),
      updateSort,
      where: []
    },
    true,
    true
  );
}

/**
 * 修改字典数据
 */
export async function updateDictionaryData(data: DictionaryData) {
  const res = await request.post<ApiResult<unknown>>(`${BASE_URL}/update`, {
    where: RParam.buildWhere({ seq: data.seq, dictSeq: data.dictSeq }),
    update: RParam.buildWhere(data)
  });
  return res.data.code === 0 ? res.data : Promise.reject(res.data);
}

/**
 * 删除字典数据
 */
export async function removeDictionaryData(params: RP) {
  const res = await request.post<ApiResult<unknown>>(
    `${BASE_URL}/delete`,
    params
  );
  return res.data.code === 0 ? res.data : Promise.reject(res.data);
}

/** 获取数据 */
async function queryByDictCode(dictCode: string) {
  if (!dictCode) {
    return [];
  }
  return listDictionaryData({ where: RParam.buildWhere({ dictCode }) }).then(
    (v) => v ?? []
  );
}

/** 查询扩展参数 */
export async function queryListProp(param: RP) {
  const res = await request.post<R<PropEntity[]>>(
    BASE_URL + '/prop/query/list',
    param
  );
  if (res.data.code === 0) {
    return res.data.data;
  }
  return Promise.reject(new Error(res.data.message));
}

/** 查询单个字典 */
async function querySingle(dictDataCode: string) {
  return LzdAjax.post<TenantDictData>(
    BASE_URL + '/query/single',
    { where: RParam.buildWhere({ dictDataCode }) },
    false,
    true
  ).then((v) => v.data);
}

/** 数据字典API */
class tenantDictDataApi {
  static queryByDictCode = queryByDictCode;
  static querySingle = querySingle;
}

export default tenantDictDataApi;
